"use client";
import { Input } from "@/ui/index";
import { Label } from "@cmp-src/primitives/label";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useAuthenticated } from "@gettactic/hooks";
import { addDays, startOfDay } from "date-fns";
import { motion } from "framer-motion";
import { ChevronRightIcon, XIcon } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { UnifiedSelect, type SelectItem } from "@cmp-src/form/UnifiedSelect";
import { type Frequency, RRule, type Weekday } from "rrule";
import { inputV2ClassesOnGray } from "./utils/classes";
import { canScheduleOthers } from "@gettactic/helpers";
import { Button } from "@cmp-src/primitives/button";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import type { DateValue } from "@cmp-src/calendar/types";

const validPeriodicityOptions: SelectItem<{
  freq: AdvancedFormState["freq"];
}>[] = [
  { id: String(RRule.DAILY), name: "Day", data: { freq: RRule.DAILY } },
  { id: String(RRule.WEEKLY), name: "Week", data: { freq: RRule.WEEKLY } },
  { id: String(RRule.MONTHLY), name: "Month", data: { freq: RRule.MONTHLY } },
  { id: String(RRule.YEARLY), name: "Year", data: { freq: RRule.YEARLY } }
];
//

const repeatOnDays: { label: string; value: Weekday }[] = [
  { label: "Sunday", value: RRule.SU },
  { label: "Monday", value: RRule.MO },
  { label: "Tuesday", value: RRule.TU },
  { label: "Wednesday", value: RRule.WE },
  { label: "Thursday", value: RRule.TH },
  { label: "Friday", value: RRule.FR },
  { label: "Saturday", value: RRule.SA }
];

type Props = {
  setAdvanced: React.Dispatch<React.SetStateAction<boolean>>;
  state: AdvancedFormState;
  setState: (x: AdvancedFormState) => void;
  hideWeekends?: boolean;
  minUntilDate: Date;
  isCompact?: boolean;
  showNeverOption?: boolean;
  maxDateAllowed?: Date;
  isCustom?: boolean;
  advanced?: boolean;
  wasNeverEnding?: boolean;
};

export type AdvancedFormState = {
  interval: number;
  freq: Frequency;
  byweekday: Weekday[];
  until: Date | null;
};

export function ReserveFieldRecurringAdvancedForm({
  setAdvanced,
  state,
  setState,
  hideWeekends,
  minUntilDate,
  isCompact = false,
  showNeverOption = true,
  maxDateAllowed,
  isCustom = false,
  advanced = false,
  wasNeverEnding = false
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);

  const [untilDate, setUntilDate] = useState(
    state.until ?? (maxDateAllowed || addDays(minUntilDate, 30))
  );

  useEffect(() => {
    if (state.until) {
      setUntilDate(state.until);
    } else if (maxDateAllowed && !state.until) {
      // If no until date is set and we have a maxDateAllowed, use it as default
      setUntilDate(maxDateAllowed);
    }
  }, [state.until, maxDateAllowed]);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="relative flex flex-col overflow-hidden rounded-lg bg-[#F7F9FB] p-4 font-inter text-muted-foreground border-1 border-neutral-100"
    >
      {/* Ends on section - always visible when recurring is selected */}
      <div className={advanced ? "order-3" : ""}>
        <div className="flex items-center justify-between">
          <div className={cn(styles.baseLabelClass, "basis-1/4")}>Ends</div>
          {!advanced && isCustom && (
            <Button
              className="flex items-center font-inter text-primary text-sm hover:text-primary-dark"
              variant="ghost"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(true);
              }}
            >
              Options <ChevronRightIcon className="h-4 w-4 rotate-90" />
            </Button>
          )}
        </div>
        <div className="flex flex-col">
          {showNeverOption && (
            <Label className="my-1 flex items-center">
              <Input
                type="radio"
                onChange={() => {
                  setState({ ...state, until: null });
                }}
                checked={state.until === null}
              />
              <div className="ml-2 text-base">Never</div>
            </Label>
          )}
          <div className={cn("flex items-center justify-between")}>
            {showNeverOption && (
              <Label className={cn("flex items-center", isCompact ? "" : "my-1")}>
                <Input
                  type="radio"
                  onChange={() => {
                    setState({ ...state, until: untilDate });
                  }}
                  checked={state.until !== null}
                />
                <div className="ml-2 text-base">On</div>
              </Label>
            )}
            <div
              className={cn(
                "flex grow basis-3/4",
                isCompact ? "ml-4" : showNeverOption ? "ml-4" : ""
              )}
            >
              <div
                className={cn(
                  "w-full [&_button]:bg-white [&_button]:!border-0 [&_button]:shadow-[0_0_0_1px_#d1d5db] [&_button]:font-normal [&_button]:text-neutral-700 [&_button_span]:font-normal [&_button_span]:text-neutral-700 [&_button]:rounded-lg [&_button]:outline-none [&_button:focus]:outline-none [&_button:focus-visible]:outline-none [&_button:focus]:ring-0 [&_button:focus-visible]:ring-0",
                  !state.until && "opacity-50"
                )}
              >
                <CalendarInput
                  mode="single"
                  selected={untilDate}
                  selectedDate={untilDate}
                  onSelect={(date: DateValue | undefined) => {
                    if (date instanceof Date) {
                      setState({ ...state, until: date });
                    }
                  }}
                  setValueOnApply={false}
                  disabled={(day: Date) => {
                    const start = startOfDay(minUntilDate);
                    const dayStart = startOfDay(day);
                    if (dayStart < start) return true;
                    if (maxDateAllowed && !isAdmin) {
                      const end = startOfDay(maxDateAllowed);
                      if (dayStart > end) return true;
                    }
                    return false;
                  }}
                />
              </div>
              {/* Show "Currently ends: Never" text when appropriate */}
              {wasNeverEnding && !showNeverOption && (
                <div className="mb-2 ml-3 text-neutral-600 text-sm">
                  Currently ends: Never
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced options - only show when advanced is true and custom recurrence is selected */}
      {advanced && isCustom && (
        <>
          <div className="order-1 flex items-center justify-between">
            <h4 className="font-medium mb-2 border-[#E5EBEE] font-inter text-[#778086] text-md">
              Options
            </h4>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(false);
              }}
            >
              <XIcon className="size-4" />
            </button>
          </div>
          <div
            className={cn(
              "mt-4 flex order-2",
              isCompact ? "flex-col items-start" : "items-center"
            )}
          >
            <div
              className={cn(
                "font-inter text-sm",
                isCompact ? "flex-1" : "basis-1/4"
              )}
            >
              Repeat every
            </div>
            <div
              className={cn(
                "flex items-center",
                isCompact ? "w-full items-start gap-1.5" : "gap-3 space-x-1"
              )}
            >
              <div className={cn("", isCompact ? "w-half" : "basis-1/4")}>
                <div className="focus-ring-rainbow">
                  <Input
                    onChange={(ev) => {
                      if (ev.currentTarget.valueAsNumber < 0) {
                        return;
                      }
                      setState({
                        ...state,
                        interval: ev.currentTarget.valueAsNumber
                      });
                    }}
                    value={state.interval.toString()}
                    type="number"
                    className={cn(
                      "resource-select !border-none pl-1 h-[42px] rounded-md shadow-none [.v2-reservation_&]:shadow-none ",
                      inputV2ClassesOnGray
                    )}
                  />
                </div>
              </div>

              <div className="focus-ring-rainbow bg-white">
                <UnifiedSelect
                  items={validPeriodicityOptions}
                  value={
                    validPeriodicityOptions.find(
                      (x) => x.data?.freq === state.freq
                    ) ?? null
                  }
                  onSelect={(item) => {
                    if (!item || Array.isArray(item)) {
                      return;
                    }
                    if (item.data?.freq !== undefined) {
                      setState({
                        ...state,
                        freq: item.data.freq,
                        byweekday:
                          item.data.freq === RRule.DAILY
                            ? state.byweekday
                            : []
                      });
                    }
                  }}
                  triggerClassName={cn(
                    "resource-select bg-white",
                    inputV2ClassesOnGray,
                    isCompact ? "w-full" : ""
                  )}
                />
              </div>
            </div>
          </div>
          {state.freq === RRule.DAILY && (
            <div
              className={cn(
                "mt-2 flex order-2",
                isCompact ? "flex-col items-start mt-4" : "items-center"
              )}
            >
              <div
                className={cn(
                  styles.baseLabelClass,
                  isCompact ? "mb-1" : "basis-1/4"
                )}
              >
                Repeat on
              </div>
              <div
                className={cn(
                  "flex items-center",
                  isCompact ? "gap-1.5" : "gap-3 space-x-1"
                )}
              >
                {repeatOnDays
                  .filter((day) =>
                    hideWeekends
                      ? !(day.value === RRule.SU || day.value === RRule.SA)
                      : true
                  )
                  .map((day) => {
                    const idx = state.byweekday.findIndex(
                      (x) => x.weekday === day.value.weekday
                    );
                    return (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: <keep the div for the click event>
                      <div
                        key={day.value.toString()}
                        onClick={((idx, value) => () => {
                          const byweekday: Weekday[] = [...state.byweekday];
                          if (idx > -1) {
                            byweekday.splice(idx, 1);
                          } else {
                            byweekday.push(value);
                          }
                          setState({ ...state, byweekday: byweekday });
                        })(idx, day.value)}
                        className={cn(
                          "font-inter flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border text-center text-[0.9rem] capitalize",
                          idx > -1
                            ? "bg-primary text-white "
                            : "border-neutral-200 text-secondary-foreground"
                        )}
                        title={day.label}
                      >
                        {day.label[0]}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}

const styles = {
  baseLabelClass: "font-inter text-sm"
};
